import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import database_en from '../data/db/database_en';
import database_es from '../data/db/database_es';

const LanguageContext = createContext();

const calculateAge = (birthDateString = '1992/09/28') => {
  const birth = new Date(birthDateString);
  const diff = new Date() - birth;
  return new Date(diff).getFullYear() - 1970;
};

const getInitialLang = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const queryLang = urlParams.get('lang');
  if (queryLang && (queryLang === 'es' || queryLang === 'en')) {
    return queryLang;
  }
  const savedLang = localStorage.getItem('user_lang');
  if (savedLang && (savedLang === 'es' || savedLang === 'en')) {
    return savedLang;
  }
  return 'es';
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(getInitialLang);
  const [content, setContent] = useState(() => {
    const initial = getInitialLang() === 'es' ? database_es : database_en;
    return { ...initial, information: { ...initial.information, age: calculateAge() } };
  });
  const [isRemote, setIsRemote] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchRemoteTranslations = useCallback(async (targetLang) => {
    // If running locally, ALWAYS use local JSON files from local disk
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      const localData = targetLang === 'es' ? database_es : database_en;
      setContent({ ...localData, information: { ...localData.information, age: calculateAge() } });
      setIsRemote(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    // Remote GitHub Raw URL for dynamic production translations
    const remoteUrl = `https://raw.githubusercontent.com/oac28x/reactjs_web_profile/main/src/data/db/database_${targetLang}.json`;

    try {
      const response = await fetch(remoteUrl, { cache: 'no-cache' });
      if (response.ok) {
        const data = await response.json();
        if (data && data.titles) {
          if (data.information) {
            data.information.age = calculateAge();
          }
          setContent(data);
          setIsRemote(true);
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      // Fallback silently to local database
    }

    // Fallback to local data
    const fallback = targetLang === 'es' ? database_es : database_en;
    setContent({ ...fallback, information: { ...fallback.information, age: calculateAge() } });
    setIsRemote(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRemoteTranslations(lang);
  }, [lang, fetchRemoteTranslations]);

  const setLanguage = (newLang) => {
    if (newLang !== 'es' && newLang !== 'en') return;
    setLangState(newLang);
    localStorage.setItem('user_lang', newLang);
  };

  return (
    <LanguageContext.Provider value={{ lang, setLanguage, d: content, isRemote, loading }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
